import { Col, Space, FormInstance } from 'antd';
import isEqual from 'lodash/isEqual';
import React, { useCallback, useEffect, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { components, OptionProps } from 'react-select';
import styled from 'styled-components';
import { Automation } from 'api/automations';
import { Checkbox } from 'components/atoms/checkbox';
import { Form } from 'components/atoms/form';
import { Radio } from 'components/atoms/radio';
import { Select } from 'components/atoms/select';
import { Option } from 'components/organisms/module-settings/fields/types';
import { RootState } from 'data';
import { useGetKeyEventMutationsForRefId } from 'hooks/use-key-events';
import { useTranslate, useTranslation } from 'hooks/use-translation';
import { ANY_STATUS_KEY } from './constant';

const KeyEventHelpText = styled.span`
  margin-top: var(--spacing-02);
`;

const CaptureExpectedDateLabel = styled.p`
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-bold);
  color: black;
  margin-bottom: 0;
  padding-bottom: 0;
`;

const FormItemWithReducedMargin = styled(Form.Item)`
  margin-bottom: 0;
  padding-bottom: var(--spacing-03) !important;
  .ant-form-item {
    padding-bottom: var(--spacing-03) !important;
  }
`;

const StyledCaptureExpectedDateLabel = styled(CaptureExpectedDateLabel)`
  margin-top: var(--spacing-05);
`;

const UpdatePlannedDateLabel = styled.p`
  margin-left: var(--spacing-02);
`;

const WhenStatusChangesWrapper = styled.p`
  font-weight: var(--font-weight-bold);
`;

const HorizontalFormItem = styled(Form.Item)`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: row;
  margin-top: var(--spacing-05);

  .ant-form-item-control-input-content {
    display: flex;
  }
`;

interface Props {
  automation?: Automation;
  refType: 'phase' | 'task';
  refId: number;
  form: FormInstance<any>;
  captureExpectedDateAutomation?: Automation;
  updateExpectedDateAutomation?: Automation;
  refName: string;
  templateId: number;
}

interface FieldProps {
  label: string;
}

export const FieldOption: React.FC<FieldProps> = ({ label }) => (
  <Space size={0}>{label}</Space>
);

const OptionComp = (props: OptionProps<Option, false>) => {
  const { data } = props;

  return (
    <components.Option {...props}>
      <FieldOption label={data.label} />
    </components.Option>
  );
};

export const KeyEventForm: React.FC<Props> = ({
  automation,
  refType,
  form,
  captureExpectedDateAutomation,
  updateExpectedDateAutomation,
  refName,
  refId,
  templateId,
}) => {
  const { data: automations } = useGetKeyEventMutationsForRefId(
    templateId,
    refType,
    refId,
  );
  const translate = useTranslate();
  const requiredMessage = useTranslation('input.required');
  const fieldStore = useSelector((state: RootState) => state.fields);
  const taskStatusFieldId = fieldStore.byObjectType.TASK.byFieldName.status;
  const ANY_STATUS_OPTION = useMemo(
    () => ({
      value: ANY_STATUS_KEY,
      label: translate('templates.tab.key_events.any_status'),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const options: { value: string; label: string }[] =
    refType === 'phase'
      ? ['To do', 'In progress', 'Completed', 'Blocked'].map((e) => ({
          value: e,
          label: e,
        }))
      : (fieldStore.byId[taskStatusFieldId][1]?.metaData as any).options.map(
          (e) => ({
            value: e.label,
            label: e.label,
          }),
        );

  options.push(ANY_STATUS_OPTION);

  const fromOptions = useCallback(
    () =>
      options.filter((e) => e.value !== form.getFieldValue('toLabel')?.value),
    [form, options],
  );

  const toOptions = useCallback(
    () =>
      options.filter((e) => e.value !== form.getFieldValue('fromLabel')?.value),
    [form, options],
  );

  const takenKeyEvents = automations
    ?.map((e) => e.actionsV2?.at(0)?.input?.fieldId)
    .filter((e) => !!e);

  const keyEventsAvailable = Object.values(fieldStore.byId).filter(
    (e) =>
      e[1]?.fieldType === 'KEY_EVENTS' &&
      !!(e[1]?.metaData as any)?.relatedFieldId &&
      !takenKeyEvents.includes(e[1]?.fieldId),
  );

  const getFormInitalValuesBasedOnAutomation = useCallback(
    () => ({
      fromLabel: automation?.triggerV2?.input.fromFieldLabel
        ? {
            value: automation?.triggerV2?.input.fromFieldLabel,
            label: automation?.triggerV2?.input.fromFieldLabel,
          }
        : ANY_STATUS_OPTION,
      toLabel: {
        value: automation?.triggerV2?.input.toFieldLabel,
        label: automation?.triggerV2?.input.toFieldLabel,
      },
      keyEventId: {
        label:
          fieldStore.byId[
            automation?.actionsV2?.at(0)?.input?.fieldId as number
          ]?.[1]?.fieldLabel,
        value:
          fieldStore.byId[
            automation?.actionsV2?.at(0)?.input?.fieldId as number
          ]?.[1]?.fieldId,
      },
      captureExpectedEvent: !!captureExpectedDateAutomation,
      startOrDueDate:
        captureExpectedDateAutomation?.triggerV2?.input?.fieldName,
      updatePlannedDate: !!updateExpectedDateAutomation,
    }),
    [
      ANY_STATUS_OPTION,
      automation?.actionsV2,
      automation?.triggerV2?.input.fromFieldLabel,
      automation?.triggerV2?.input.toFieldLabel,
      captureExpectedDateAutomation,
      fieldStore.byId,
      updateExpectedDateAutomation,
    ],
  );

  useEffect(() => {
    if (
      !isEqual(form.getFieldsValue(), getFormInitalValuesBasedOnAutomation()) &&
      !!automation
    ) {
      form.setFieldsValue(getFormInitalValuesBasedOnAutomation());
    }
  }, [
    captureExpectedDateAutomation,
    updateExpectedDateAutomation,
    automation,
    form,
    getFormInitalValuesBasedOnAutomation,
  ]);

  useEffect(() => {
    if (
      form.getFieldValue('toLabel')?.value ===
      form.getFieldValue('fromLabel')?.value
    ) {
      form.setFieldsValue({
        ...form.getFieldsValue(),
        toLabel: undefined,
      });
    }
  }, [form]);

  return (
    <Form
      form={form}
      layout="vertical"
      initialValues={
        automation !== undefined
          ? getFormInitalValuesBasedOnAutomation()
          : {
              captureExpectedEvent: true,
              startOrDueDate: 'startDate',
              fromLabel: ANY_STATUS_OPTION,
            }
      }
      preserve={false}
    >
      {() => {
        return (
          <>
            <WhenStatusChangesWrapper>
              <FormattedMessage
                id="key_event.when_status_changes"
                values={{ refType }}
              />
            </WhenStatusChangesWrapper>
            <Col span={36}>
              <Form.Item
                name="fromLabel"
                shouldUpdate
                label={<FormattedMessage id="from" />}
                rules={[
                  {
                    required: true,
                    message: requiredMessage,
                  },
                ]}
              >
                <Select
                  options={fromOptions()}
                  components={{ Option: OptionComp }}
                />
              </Form.Item>
            </Col>
            <Col span={36}>
              <Form.Item
                required
                name="toLabel"
                shouldUpdate
                label={<FormattedMessage id="to" />}
                rules={[
                  {
                    required: true,
                    message: requiredMessage,
                  },
                ]}
              >
                <Select
                  options={toOptions()}
                  components={{ Option: OptionComp }}
                />
              </Form.Item>
            </Col>
            <Col span={36}>
              <FormItemWithReducedMargin
                required
                name="keyEventId"
                shouldUpdate
                rules={[
                  {
                    required: true,
                    message: requiredMessage,
                  },
                ]}
                label={
                  <CaptureExpectedDateLabel>
                    <FormattedMessage id="record_key_event" />
                  </CaptureExpectedDateLabel>
                }
                help={
                  <KeyEventHelpText>
                    <FormattedMessage id="templates.tab.key_events.capture_key_event.help_text" />
                  </KeyEventHelpText>
                }
              >
                <Select
                  options={keyEventsAvailable?.map((e) => ({
                    value: e[1]?.fieldId,
                    label: e[1]?.fieldLabel,
                  }))}
                  components={{ Option: OptionComp }}
                />
              </FormItemWithReducedMargin>

              {form.getFieldValue('keyEventId') ? (
                <FormItemWithReducedMargin
                  shouldUpdate
                  name="captureExpectedEvent"
                  label={
                    <StyledCaptureExpectedDateLabel>
                      <FormattedMessage id="templates.tab.key_events.capture_expected_event" />
                    </StyledCaptureExpectedDateLabel>
                  }
                >
                  <Radio.Group>
                    <Radio value>
                      <FormattedMessage id="yes" />
                    </Radio>
                    <Radio value={false}>
                      <FormattedMessage id="no" />
                    </Radio>
                  </Radio.Group>
                </FormItemWithReducedMargin>
              ) : null}

              {form.getFieldValue('keyEventId') &&
              form.getFieldValue('captureExpectedEvent') ? (
                <FormItemWithReducedMargin
                  shouldUpdate
                  name="startOrDueDate"
                  label={
                    <StyledCaptureExpectedDateLabel>
                      <FormattedMessage
                        id="templates.tab.key_events.capture_expected_event_start_due_date"
                        values={{
                          fieldLabel: refName,
                        }}
                      />
                    </StyledCaptureExpectedDateLabel>
                  }
                >
                  <Radio.Group>
                    <Radio value="startDate">
                      <FormattedMessage id="start_date" />
                    </Radio>
                    <Radio value="dueDate">
                      <FormattedMessage id="due_date" />
                    </Radio>
                  </Radio.Group>
                </FormItemWithReducedMargin>
              ) : null}
              {form.getFieldValue('keyEventId') &&
              form.getFieldValue('captureExpectedEvent') ? (
                <HorizontalFormItem name="updatePlannedDate">
                  <Checkbox
                    defaultChecked={form.getFieldValue('updatePlannedDate')}
                    onChange={() => {
                      form.setFieldsValue({
                        ...form.getFieldsValue(),
                        updatePlannedDate:
                          !form.getFieldValue('updatePlannedDate'),
                      });
                    }}
                  />
                  <UpdatePlannedDateLabel>
                    <FormattedMessage id="templates.tab.key_events.capture_expected_event_on_update" />
                  </UpdatePlannedDateLabel>
                </HorizontalFormItem>
              ) : null}
            </Col>
          </>
        );
      }}
    </Form>
  );
};
